// YesNoToggle (DESIGN §3.9.6): drei Kreise 28 (mobil 44) ○ offen · ✕ No · ✓ Yes. Gewählt: No →
// `danger` mit Glyph `on-danger-solid`, Yes → `success` mit Glyph `on-grade-a`; nicht gewählt
// `pill-tile` mit Glyph `fg`; ○ mit Rand 1,5 px `fg-tertiary`.
// Tastatur: Y/N setzen, 0/Entf/Backspace löscht, ←/→ wandern, ↑/↓ springen zur vorigen/nächsten
// Aussage innerhalb von `[data-yesno-list]` (SPEC F-scorecard-14). ARIA: radiogroup.
import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { CheckIcon, CircleIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '../basis/kern';

export type YesNoValue = 'yes' | 'no' | null;

const OPTIONS: Array<{ value: YesNoValue; key: string; label: string }> = [
  { value: null, key: 'none', label: 'Not answered' },
  { value: 'no', key: 'no', label: 'No' },
  { value: 'yes', key: 'yes', label: 'Yes' },
];

export interface YesNoToggleProps {
  value: YesNoValue;
  onValueChange?: (value: YesNoValue) => void;
  /** Zugänglicher Name = Text der Aussage. */
  'aria-label': string;
  /** Red flag (Poor-Aussage): „No“ ist gut. */
  redFlag?: boolean;
  /** Gesperrt (abgeschlossener Zyklus): keine Änderung, gewählte bleiben farbig. */
  readOnly?: boolean;
  className?: string;
}

const GROUP_SELECTOR = '[data-yesno-group]';

/** Fokus auf die vorige/nächste Aussage derselben Liste (gewählte Option bzw. erste). */
function focusSibling(group: HTMLElement, delta: 1 | -1) {
  const scope = group.closest('[data-yesno-list]') ?? document;
  const groups = Array.from(scope.querySelectorAll<HTMLElement>(GROUP_SELECTOR));
  const target = groups[groups.indexOf(group) + delta];
  target?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
}

export function YesNoToggle({
  value,
  onValueChange,
  redFlag = false,
  readOnly = false,
  className,
  ...aria
}: YesNoToggleProps) {
  const groupRef = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const set = (next: YesNoValue) => {
    if (!readOnly && next !== value) onValueChange?.(next);
  };
  const focusOption = (key: string) =>
    groupRef.current?.querySelector<HTMLElement>(`[data-option="${key}"]`)?.focus();

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const index = OPTIONS.findIndex((o) => o.value === value);
    const key = event.key.toLowerCase();
    let next: YesNoValue | undefined;
    if (key === 'y') next = 'yes';
    else if (key === 'n') next = 'no';
    else if (key === '0' || key === 'delete' || key === 'backspace') next = null;
    else if (key === 'arrowright' || key === 'arrowleft') {
      const step = key === 'arrowright' ? 1 : -1;
      next = OPTIONS[(index + step + OPTIONS.length) % OPTIONS.length]?.value ?? null;
    } else if ((key === 'arrowdown' || key === 'arrowup') && groupRef.current) {
      event.preventDefault();
      focusSibling(groupRef.current, key === 'arrowdown' ? 1 : -1);
      return;
    } else return;
    event.preventDefault();
    set(next);
    focusOption(OPTIONS.find((o) => o.value === next)?.key ?? 'none');
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={aria['aria-label']}
      aria-describedby={redFlag ? hintId : undefined}
      tabIndex={-1}
      aria-readonly={readOnly || undefined}
      data-yesno-group=""
      onKeyDown={onKeyDown}
      className={cn(
        'inline-flex shrink-0 items-center gap-2 focus-visible:outline-none',
        className,
      )}
    >
      {redFlag ? (
        <span id={hintId} className="sr-only">
          Red flag, no is good
        </span>
      ) : null}
      {OPTIONS.map((option) => {
        const checked = option.value === value;
        const tone =
          option.value === null
            ? 'border-[1.5px] border-fg-tertiary bg-transparent text-fg'
            : checked && option.value === 'no'
              ? 'bg-danger text-on-danger-solid'
              : checked && option.value === 'yes'
                ? 'bg-success text-on-grade-a'
                : 'bg-pill-tile text-fg';
        const glyph: ReactNode =
          option.value === null ? (
            <CircleIcon weight="bold" />
          ) : option.value === 'no' ? (
            <XIcon weight="bold" />
          ) : (
            <CheckIcon weight="bold" />
          );
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.label}
            data-option={option.key}
            data-state={checked ? 'checked' : 'unchecked'}
            tabIndex={checked ? 0 : -1}
            aria-disabled={readOnly || undefined}
            onClick={() => set(option.value)}
            className={cn(
              'relative inline-flex size-7 items-center justify-center rounded-full max-md:size-11',
              'transition-[background-color,color,transform] duration-(--bar-dur-fast) ease-standard',
              '[&>svg]:size-3.5 max-md:[&>svg]:size-4.5',
              option.value === null && checked && 'ring-2 ring-fg-tertiary/40',
              !readOnly && !checked && 'hover:bg-pill-tile-hover active:scale-[.94]',
              readOnly && 'cursor-default',
              tone,
            )}
          >
            {glyph}
          </button>
        );
      })}
    </div>
  );
}
